import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { useMountEffect } from "primereact/hooks";
import { Button } from "primereact/button";
import { useIntl } from "react-intl";
import { sectionListRequest } from "../../../../store/slices/customFieldSlice";
import {
  departmentRequest,
  designationRequest,
} from "../../../../store/slices/metaSlice";
import { ListBox } from "primereact/listbox";
import { GlobalConstants } from "../../../../constants";
import { MultiSelect } from "primereact/multiselect";

import "./user-filter.scss";

import FiltersSheet from "./FiltersSheet";
import useMobileView from "../../../common/hooks/useMobileView";
import {
  useOverlayEscape,
  useOverlayPanelEscape,
} from "../../../common/fields/useOverlayPanelEscape";

// A filter's values. Escape closes its panel without closing a Dialog around
// the filters (the certificate dialog's New download), like the shared fields.
const FilterValues = (props) => {
  const componentRef = useRef(null);
  const panelEscape = useOverlayPanelEscape(componentRef);

  return (
    <MultiSelect
      {...props}
      ref={componentRef}
      onShow={panelEscape.onShow}
      onHide={panelEscape.onHide}
    />
  );
};

export const UserFilters = ({
  departmentRequest,
  departments = [],
  designationRequest,
  designations = [],
  sectionListRequest,
  sections,
  onFilterChange = () => {},
  filters = [],
}) => {
  const intl = useIntl();
  const { isMobile } = useMobileView();

  const [isOpen, setIsOpen] = useState(false);
  const [isFilterSheetVisible, setIsFilterSheetVisible] = useState(false);
  const ref = useRef(null);

  // Escape closes the open Add filter list, and only the list when the
  // filters are inside a Dialog (the certificate dialog's New download).
  const closeAddFilter = useCallback(() => setIsOpen(false), []);
  useOverlayEscape(isOpen, closeAddFilter);
  const closeFilterSheet = useCallback(() => setIsFilterSheetVisible(false), []);

  const handleOutsideClick = (event) => {
    if (ref?.current && !ref?.current?.contains(event.target)) {
      setIsOpen(false); // Close the dropdown
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isFilterSheetVisible && !isMobile) {
      setIsFilterSheetVisible(false);
    }
  }, [isMobile]);

  useMountEffect(() => {
    departmentRequest();
    designationRequest();
    sectionListRequest();
  });

  // Memoised so the filters sheet (which resets its selections when its filters change)
  // gets the same filters until something it shows really changes.
  const allFields = useMemo(() => {
    const customFields = sections.reduce((acc, section) => {
      acc = [
        ...acc,
        ...(section?.fields?.map((item) => ({
          id: item.id,
          // The label, as the report columns show it; the name is the
          // field's internal key (B-BUG-7).
          name: item?.label || item?.name,
          value: item.id,
          type: item.type,
          options: item?.options?.map((option) => ({
            id: option,
            name: option,
            value: option,
          })),
        })) || []),
      ];
      return acc;
    }, []);

    return [
      {
        id: "Designation",
        name: intl.formatMessage({ id: "designation" }),
        value: "Designation",
        type: "Dropdown",
        i18n: true,
        options: designations?.map((item) => ({
          name: item?.name || item.value,
          id: item.id,
          i18n: false,
          value: item.id,
        })),
      },
      {
        id: "Department",
        name: intl.formatMessage({ id: "department" }),
        value: "Department",
        type: "Dropdown",
        i18n: true,
        options: departments?.map((item) => ({
          name: item.name || item.value,
          id: item.id,
          i18n: false,
          value: item.id,
        })),
      },
      ...customFields
        .filter((item) => item.type !== 5)
        .map((item) => ({
          ...item,
          type: GlobalConstants.FIELD_TYPES.find((type) => type.id === item.type)
            ?.name,
        })),
    ];
  }, [designations, departments, sections, intl]);

  // Filters restored from a saved report carry only their id and values;
  // take the name and options from the tenant's fields.
  const shownFilters = useMemo(
    () =>
      filters.map((filter) => {
        const field = allFields.find((item) => item.id === filter.id);
        return {
          ...filter,
          name: field?.name || filter.name,
          type: field?.type || filter.type,
          options: field?.options || filter.options || [],
        };
      }),
    [filters, allFields]
  );

  const availableFields = allFields.filter(
    (item) => !filters.find((field) => field.id === item.id)
  );

  return (
    <>
      <div ref={ref} className="edexlms-filters users">
        <div className="hidden md:block">
          {(availableFields.length > 0 && (
            <>
              <Button
                outlined
                onClick={() => setIsOpen(!isOpen)}
                label={intl.formatMessage({ id: "add_filter" })}
                icon="pi pi-plus"
              />
              {(availableFields.length > 0 && isOpen && (
                <div className="edexlms-listbox-container">
                  <ListBox
                    className="edexlms-listbox"
                    options={availableFields}
                    optionLabel="name"
                    optionValue="id"
                    listStyle={{ maxHeight: "250px" }}
                    onChange={(e) => {
                      const field = allFields.find(
                        (item) => item.id === e.value
                      );
                      if (filters.find((item) => item.id === field.id)) return;
                      setIsOpen(false);
                      onFilterChange([
                        ...filters,
                        {
                          id: field?.id,
                          options: field?.options,
                          type: field?.type,
                          name: field?.name,
                          values: [],
                        },
                      ]);
                    }}
                  />
                </div>
              )) ||
                null}
            </>
          )) ||
            null}
        </div>
        <div className="md:hidden">
          <Button
            onClick={() => setIsFilterSheetVisible(true)}
            className="flex align-items-center gap-1 relative overflow-visible"
          >
            {filters.filter((item) => item.values.length > 0).length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "red",
                  width: 20,
                  height: 20,
                  borderRadius: "100%",
                }}
              >
                <span>
                  {filters.filter((item) => item.values.length > 0).length}
                </span>
              </div>
            )}
            Filters
          </Button>
        </div>
      </div>
      <div className="hidden md:flex flex-wrap gap-2">
        {shownFilters.map((field, index) => {
          return (
            <div className="edexlms-filter-item" key={index}>
              <FilterValues
                value={field.values}
                filter={true}
                options={field.options.filter((option) => option.id)}
                optionLabel="name"
                optionValue="id"
                fixedPlaceholder={true}
                placeholder={field?.name}
                label={field?.name}
                onChange={(e) => {
                  const values = e.value;
                  onFilterChange(
                    filters.map((item) =>
                      item.id === field.id ? { ...item, values: values } : item
                    )
                  );
                }}
              />
              <Button
                outlined
                icon="pi pi-times"
                onClick={() =>
                  onFilterChange(filters.filter((item) => item.id !== field.id))
                }
              />
            </div>
          );
        })}
      </div>
      {isFilterSheetVisible && (
        <FiltersSheet
          allFields={allFields}
          filters={shownFilters}
          handleClose={closeFilterSheet}
          isFilterSheetVisible={isFilterSheetVisible}
          onFilterChange={onFilterChange}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  departments: state.meta.departments,
  designations: state.meta.designations,
  sections: state.customFields.data,
});

const mapDispatchToProps = (dispatch) => ({
  sectionListRequest: () => dispatch(sectionListRequest()),
  departmentRequest: () => dispatch(departmentRequest()),
  designationRequest: () => dispatch(designationRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserFilters);
